import { GoogleOAuthProvider } from '@react-oauth/google';
import NextApp from 'next/app';
import { useRouter } from 'next/router';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { ToastContainer } from '~/common/components/ToastContainer/ToastContainer';
import { AuthenticationProvider } from '~/components/AuthenticationProvider/AuthenticationProvider';
import { BrainfishButton } from '~/components/Brainfish/Brainfish';
import { EppoProvider } from '~/components/Eppo/EppoProvider';
import { getFeatureFlags } from '~/components/FeatureFlags/utils';
import { Layout } from '~/components/Layout/Layout';
import { ResponsiveContextProvider } from '~/components/ResponsiveContextProvider/ResponsiveContextProvider';
import { SettingsProvider } from '~/components/SettingsProvider/SettingsProvider';
import { SharedAppStateProvider } from '~/components/SharedAppState/ShareAppStateProvider';
import { AnalyticsPageTracker } from '~/components/analytics/AnalyticsPageTracker/AnalyticsPageTracker';
import { AnalyticsProvider } from '~/components/analytics/AnalyticsProvider';
import { UserApi } from '~/services/user/UserApi';
import { getAuthorizationHeader } from '~/utils/auth';
import { shouldShowChatWidget } from '~/utils/chatWidget';
import { inferScreenWidthFromUserAgent, isCrawlerRequest } from '~/utils/userAgent';
import { getWafTrafficMetadata } from '~/utils/wafSuspiciousTraffic';

import type { AxiosError } from 'axios';
import type { Request } from 'express';
import type { NextPageContext } from 'next';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import type { ReactElement } from 'react';
import type { FeatureFlag } from '~/common/types/settings';
import type { UserDetails } from '~/common/types/user';
import type { WafTrafficMetadata } from '~/utils/wafSuspiciousTraffic';

import '@madpaws/design-system/dist/global.css';
import '@madpaws/design-system/dist/components.css';
import './global.css';

type CustomProps = {
  featureFlags: FeatureFlag[];
  inferredScreenWidth: number;
  isCrawler: boolean;
  userDetails: UserDetails | null;
  wafTrafficMetadata: WafTrafficMetadata;
};

type Props = AppProps & CustomProps;

const App = ({
  Component,
  pageProps,
  featureFlags,
  inferredScreenWidth,
  isCrawler,
  wafTrafficMetadata,
  userDetails,
}: Props): ReactElement => {
  const router = useRouter();

  return (
    <ResponsiveContextProvider inferredScreenWidth={inferredScreenWidth}>
      <SharedAppStateProvider featureFlags={featureFlags}>
        <SettingsProvider>
          <AnalyticsProvider
            isCrawler={isCrawler}
            routeName={router.pathname}
            userId={userDetails?.id}
            wafTrafficMetadata={wafTrafficMetadata}
          >
            <AnalyticsPageTracker>
              <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_SDK_CLIENT_ID || ''}>
                <EppoProvider>
                  <GoogleReCaptchaProvider
                    reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
                  >
                    <AuthenticationProvider route={router.pathname} userDetails={userDetails}>
                      <Layout from={router.query.from} route={router.pathname}>
                        <Component {...pageProps} />
                        {shouldShowChatWidget(
                          inferredScreenWidth,
                          router.pathname,
                          router.query.from
                        ) && <BrainfishButton />}
                      </Layout>
                      <ToastContainer />
                    </AuthenticationProvider>
                  </GoogleReCaptchaProvider>
                </EppoProvider>
              </GoogleOAuthProvider>
            </AnalyticsPageTracker>
          </AnalyticsProvider>
        </SettingsProvider>
      </SharedAppStateProvider>
    </ResponsiveContextProvider>
  );
};

type InitialProps = AppInitialProps & CustomProps;

App.getInitialProps = async (appContext: AppContext & NextPageContext): Promise<InitialProps> => {
  const appProps = await NextApp.getInitialProps(appContext);
  const inferredScreenWidth = inferScreenWidthFromUserAgent(appContext.ctx.req);
  const isCrawler = isCrawlerRequest(appContext.ctx.req);
  // AWS WAF supplies this on the SSR request. It is passed into the client so
  // page and track events can be compared with the WAF decision later.
  const wafTrafficMetadata = getWafTrafficMetadata(appContext.ctx.req?.headers);
  let featureFlags: FeatureFlag[] = [];

  await getFeatureFlags()
    .then((flags) => {
      featureFlags = flags;
    })
    .catch(() => {
      console.error('Error fetching feature flags');
    });

  const authHeader = getAuthorizationHeader(appContext.ctx.req as Request);
  let userDetails = null;

  if (authHeader?.authorization) {
    try {
      // Keep this SSR request scoped; shared Axios defaults can mix users.
      userDetails = await UserApi.getUserDetails(authHeader);
    } catch (error) {
      console.error('Could not fetch user details:', (error as AxiosError)?.status);
    }
  }

  return {
    ...appProps,
    featureFlags,
    inferredScreenWidth,
    isCrawler,
    wafTrafficMetadata,
    userDetails,
  };
};

export default App;
